/**
 * LeagueManagerTab - Admin interface for managing leagues
 * Supports leagues, conferences, divisions, teams, standings, and playoffs
 * 
 * REFACTORED: Components extracted to ./league-manager/ subdirectory
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy, Plus, Edit2, Trash2, Loader2, Shield, BarChart2, Calendar,
  GitBranch, Users, X
} from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";

// Import extracted components
import { LeagueModal, ConferenceModal, DivisionModal, PlayoffModal, TeamModal } from "./league-manager/Modals";
import { LeagueDetailsSection } from "./league-manager/LeagueDetailsSection";
import { LeagueStructureSection } from "./league-manager/LeagueStructureSection";
import { LeagueTeamsSection } from "./league-manager/LeagueTeamsSection";
import { LeagueStandingsSection } from "./league-manager/LeagueStandingsSection";
import { LeagueScheduleSection } from "./league-manager/LeagueScheduleSection";
import { LeaguePlayoffsSection } from "./league-manager/LeaguePlayoffsSection";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const LeagueManagerTab = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("details"); // details, structure, teams, standings, playoffs
  
  // Modal states
  const [showLeagueModal, setShowLeagueModal] = useState(false);
  const [showConferenceModal, setShowConferenceModal] = useState(false);
  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showPlayoffModal, setShowPlayoffModal] = useState(false);
  
  // Edit states
  const [editingLeague, setEditingLeague] = useState(null);
  const [editingConference, setEditingConference] = useState(null);
  const [editingDivision, setEditingDivision] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingPlayoff, setEditingPlayoff] = useState(null);

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    try {
      const res = await axios.get(`${API}/leagues`, { headers: getAuthHeader() });
      setLeagues(res.data);
    } catch (err) {
      setError("Failed to load leagues");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagueDetails = async (leagueId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/leagues/${leagueId}`, { headers: getAuthHeader() });
      setSelectedLeague(res.data);
    } catch (err) {
      setError("Failed to load league details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLeague = (league) => {
    fetchLeagueDetails(league.id);
    setActiveSection("details");
  };

  // League CRUD
  const handleSaveLeague = async (leagueData) => {
    setSaving(true);
    try {
      if (editingLeague?.id) {
        await axios.put(`${API}/leagues/${editingLeague.id}`, leagueData, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/leagues`, leagueData, { headers: getAuthHeader() });
      }
      await fetchLeagues();
      if (selectedLeague?.id) {
        await fetchLeagueDetails(selectedLeague.id);
      }
      setShowLeagueModal(false);
      setEditingLeague(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save league");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLeague = async (leagueId) => {
    if (!confirm("Delete this league and ALL its data (conferences, divisions, teams, standings, playoffs)?")) return;
    try {
      await axios.delete(`${API}/leagues/${leagueId}`, { headers: getAuthHeader() });
      setLeagues(leagues.filter(l => l.id !== leagueId));
      if (selectedLeague?.id === leagueId) {
        setSelectedLeague(null);
      }
    } catch (err) {
      setError("Failed to delete league");
    }
  };

  // Toggle league menu visibility
  const handleToggleMenuVisibility = async (league) => {
    try {
      const updatedData = {
        ...league,
        show_in_menu: !league.show_in_menu
      };
      await axios.put(`${API}/leagues/${league.id}`, updatedData, { headers: getAuthHeader() });
      await fetchLeagues();
      if (selectedLeague?.id === league.id) {
        setSelectedLeague({ ...selectedLeague, show_in_menu: !league.show_in_menu });
      }
    } catch (err) {
      setError("Failed to update menu visibility");
    }
  };

  // Conference CRUD
  const handleSaveConference = async (confData) => {
    setSaving(true);
    try {
      if (editingConference?.id) {
        await axios.put(
          `${API}/leagues/${selectedLeague.id}/conferences/${editingConference.id}`,
          confData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/leagues/${selectedLeague.id}/conferences`,
          confData,
          { headers: getAuthHeader() }
        );
      }
      await fetchLeagueDetails(selectedLeague.id);
      setShowConferenceModal(false);
      setEditingConference(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save conference");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConference = async (confId) => {
    if (!confirm("Delete this conference? Teams and divisions will be unassigned.")) return;
    try {
      await axios.delete(
        `${API}/leagues/${selectedLeague.id}/conferences/${confId}`,
        { headers: getAuthHeader() }
      );
      await fetchLeagueDetails(selectedLeague.id);
    } catch (err) {
      setError("Failed to delete conference");
    }
  };

  // Division CRUD
  const handleSaveDivision = async (divData) => {
    setSaving(true);
    try {
      if (editingDivision?.id) {
        await axios.put(
          `${API}/leagues/${selectedLeague.id}/divisions/${editingDivision.id}`,
          divData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/leagues/${selectedLeague.id}/divisions`,
          divData,
          { headers: getAuthHeader() }
        );
      }
      await fetchLeagueDetails(selectedLeague.id);
      setShowDivisionModal(false);
      setEditingDivision(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save division");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDivision = async (divId) => {
    if (!confirm("Delete this division? Teams will be unassigned.")) return;
    try {
      await axios.delete(
        `${API}/leagues/${selectedLeague.id}/divisions/${divId}`,
        { headers: getAuthHeader() }
      );
      await fetchLeagueDetails(selectedLeague.id);
    } catch (err) {
      setError("Failed to delete division");
    }
  };

  // Team CRUD
  const handleSaveTeam = async (teamData) => {
    setSaving(true);
    try {
      if (editingTeam?.id) {
        await axios.put(
          `${API}/leagues/${selectedLeague.id}/teams/${editingTeam.id}`,
          teamData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/leagues/${selectedLeague.id}/teams`,
          teamData,
          { headers: getAuthHeader() }
        );
      }
      await fetchLeagueDetails(selectedLeague.id);
      setShowTeamModal(false);
      setEditingTeam(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save team");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm("Delete this team and its standings?")) return;
    try {
      await axios.delete(
        `${API}/leagues/${selectedLeague.id}/teams/${teamId}`,
        { headers: getAuthHeader() }
      );
      await fetchLeagueDetails(selectedLeague.id);
    } catch (err) {
      setError("Failed to delete team");
    }
  };

  // Standings update
  const handleUpdateStandings = async (standings) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/leagues/${selectedLeague.id}/standings`,
        standings,
        { headers: getAuthHeader() }
      );
      await fetchLeagueDetails(selectedLeague.id);
    } catch (err) {
      setError("Failed to update standings");
    } finally {
      setSaving(false);
    }
  };

  // Playoff CRUD
  const handleSavePlayoff = async (playoffData) => {
    setSaving(true);
    try {
      if (editingPlayoff?.id) {
        await axios.put(
          `${API}/leagues/${selectedLeague.id}/playoffs/${editingPlayoff.id}`,
          playoffData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API}/leagues/${selectedLeague.id}/playoffs`,
          playoffData,
          { headers: getAuthHeader() }
        );
      }
      await fetchLeagueDetails(selectedLeague.id);
      setShowPlayoffModal(false);
      setEditingPlayoff(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save playoff bracket");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlayoff = async (bracketId) => {
    if (!confirm("Delete this playoff bracket?")) return;
    try {
      await axios.delete(
        `${API}/leagues/${selectedLeague.id}/playoffs/${bracketId}`,
        { headers: getAuthHeader() }
      );
      await fetchLeagueDetails(selectedLeague.id);
    } catch (err) {
      setError("Failed to delete playoff bracket");
    }
  };

  if (loading && !selectedLeague) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="league-manager-tab">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Trophy className="w-7 h-7 text-orange-500" />
            League Manager
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Create and manage leagues, conferences, divisions, teams, and playoffs
          </p>
        </div>
        <Button
          onClick={() => { setEditingLeague(null); setShowLeagueModal(true); }}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="create-league-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create League
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* League List Sidebar */}
        <div className="lg:col-span-1 space-y-2">
          <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
            All Leagues
          </h3>
          {leagues.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No leagues yet</p>
              <p className="text-sm">Create your first league to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leagues.map(league => (
                <div
                  key={league.id}
                  onClick={() => handleSelectLeague(league)}
                  className={`p-3 rounded-lg cursor-pointer transition-all border ${
                    selectedLeague?.id === league.id
                      ? "bg-orange-500/20 border-orange-500/50"
                      : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  }`}
                  data-testid={`league-item-${league.id}`}
                >
                  <div className="flex items-center gap-3">
                    {league.logo_url ? (
                      <img src={league.logo_url} alt="" className="w-10 h-10 object-contain" />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: league.color || "#f97316" }}
                      >
                        <Trophy className="w-5 h-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate">{league.name}</h4>
                      <p className="text-xs text-zinc-500">{league.sport || "Multiple Sports"}</p>
                    </div>
                    {!league.is_active && (
                      <span className="text-xs bg-zinc-700 text-zinc-400 px-2 py-0.5 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          {selectedLeague ? (
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              {/* League Header */}
              <div
                className="p-6 border-b border-zinc-800"
                style={{ backgroundColor: `${selectedLeague.color}15` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {selectedLeague.logo_url ? (
                      <img src={selectedLeague.logo_url} alt="" className="w-16 h-16 object-contain" />
                    ) : (
                      <div
                        className="w-16 h-16 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: selectedLeague.color || "#f97316" }}
                      >
                        <Trophy className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-white">{selectedLeague.name}</h3>
                      <p className="text-zinc-400 text-sm">
                        {selectedLeague.sport} • {selectedLeague.season || "Current Season"}
                      </p>
                      <p className="text-zinc-500 text-xs mt-1">
                        {selectedLeague.teams?.length || 0} teams • {selectedLeague.conferences?.length || 0} conferences • {selectedLeague.divisions?.length || 0} divisions
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEditingLeague(selectedLeague); setShowLeagueModal(true); }}
                    >
                      <Edit2 className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-400/50 hover:bg-red-500/20"
                      onClick={() => handleDeleteLeague(selectedLeague.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Section Tabs */}
              <div className="border-b border-zinc-800">
                <div className="flex overflow-x-auto">
                  {[
                    { id: "details", label: "Details", icon: Trophy },
                    { id: "structure", label: "Structure", icon: GitBranch },
                    { id: "teams", label: "Teams", icon: Users },
                    { id: "standings", label: "Standings", icon: BarChart2 },
                    { id: "schedule", label: "Schedule", icon: Calendar },
                    { id: "playoffs", label: "Playoffs", icon: Shield },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSection(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                        activeSection === tab.id
                          ? "text-orange-500 border-b-2 border-orange-500"
                          : "text-zinc-400 hover:text-white"
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Section Content */}
              <div className="p-6">
                {activeSection === "details" && (
                  <LeagueDetailsSection 
                    league={selectedLeague} 
                    onToggleMenuVisibility={handleToggleMenuVisibility}
                  />
                )}
                {activeSection === "structure" && (
                  <LeagueStructureSection
                    league={selectedLeague}
                    onAddConference={() => { setEditingConference(null); setShowConferenceModal(true); }}
                    onEditConference={(conf) => { setEditingConference(conf); setShowConferenceModal(true); }}
                    onDeleteConference={handleDeleteConference}
                    onAddDivision={() => { setEditingDivision(null); setShowDivisionModal(true); }}
                    onEditDivision={(div) => { setEditingDivision(div); setShowDivisionModal(true); }}
                    onDeleteDivision={handleDeleteDivision}
                  />
                )}
                {activeSection === "teams" && (
                  <LeagueTeamsSection
                    league={selectedLeague}
                    onAddTeam={() => { setEditingTeam(null); setShowTeamModal(true); }}
                    onEditTeam={(team) => { setEditingTeam(team); setShowTeamModal(true); }}
                    onDeleteTeam={handleDeleteTeam}
                  />
                )}
                {activeSection === "standings" && (
                  <LeagueStandingsSection
                    league={selectedLeague}
                    onSave={handleUpdateStandings}
                    saving={saving}
                  />
                )}
                {activeSection === "schedule" && (
                  <LeagueScheduleSection
                    league={selectedLeague}
                    onRefresh={() => fetchLeagueDetails(selectedLeague.id)}
                  />
                )}
                {activeSection === "playoffs" && (
                  <LeaguePlayoffsSection
                    league={selectedLeague}
                    onAddPlayoff={() => { setEditingPlayoff(null); setShowPlayoffModal(true); }}
                    onEditPlayoff={(playoff) => { setEditingPlayoff(playoff); setShowPlayoffModal(true); }}
                    onDeletePlayoff={handleDeletePlayoff}
                    onRefresh={() => fetchLeagueDetails(selectedLeague.id)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-12 text-center">
              <Trophy className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Select a League</h3>
              <p className="text-zinc-500 mb-6">
                Choose a league from the list to view and manage its details
              </p>
              <Button
                onClick={() => { setEditingLeague(null); setShowLeagueModal(true); }}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New League
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <LeagueModal
        isOpen={showLeagueModal}
        onClose={() => { setShowLeagueModal(false); setEditingLeague(null); }}
        onSave={handleSaveLeague}
        league={editingLeague}
        saving={saving}
      />
      <ConferenceModal
        isOpen={showConferenceModal}
        onClose={() => { setShowConferenceModal(false); setEditingConference(null); }}
        onSave={handleSaveConference}
        conference={editingConference}
        saving={saving}
        leagueColor={selectedLeague?.color}
      />
      <DivisionModal
        isOpen={showDivisionModal}
        onClose={() => { setShowDivisionModal(false); setEditingDivision(null); }}
        onSave={handleSaveDivision}
        division={editingDivision}
        conferences={selectedLeague?.conferences || []}
        saving={saving}
        leagueColor={selectedLeague?.color}
      />
      <TeamModal
        isOpen={showTeamModal}
        onClose={() => { setShowTeamModal(false); setEditingTeam(null); }}
        onSave={handleSaveTeam}
        team={editingTeam}
        conferences={selectedLeague?.conferences || []}
        divisions={selectedLeague?.divisions || []}
        saving={saving}
      />
      <PlayoffModal
        isOpen={showPlayoffModal}
        onClose={() => { setShowPlayoffModal(false); setEditingPlayoff(null); }}
        onSave={handleSavePlayoff}
        playoff={editingPlayoff}
        saving={saving}
        conferences={selectedLeague?.conferences || []}
      />
    </div>
  );
};

export default LeagueManagerTab;
